<script setup>
/**
 * One case as a board card: title, number, priority, owner, and age.
 *
 * The card is the column's unit of drag, so it renders no stage of its own —
 * the column it sits in is the stage. Like the list row, the whole card is
 * the click target while the title stays a real link, so the card is
 * reachable with Tab and opens with Enter without a keydown handler
 * pretending to be one.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import OwnerChip from './OwnerChip.vue';
import {
  casePriorityMeta,
  isOverdueInStage,
  lastActivityLabel,
  presentOwner,
} from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  supportCase: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const owner = computed(() => presentOwner(props.supportCase));

const priority = computed(() => casePriorityMeta(props.supportCase.priority));

// How long the ticket has existed — the board's scanning signal for what has
// been sitting, where the list's column is "last activity".
const age = computed(() => lastActivityLabel(props.supportCase.createdAt));

// The stage's SLA breach, computed against render time — a fresh page or a
// board refetch is what moves it, which is all a soft signal needs.
const isOverdue = computed(() => isOverdueInStage(props.supportCase));

const linkTo = computed(() => ({
  name: 'support_cases_show',
  params: { displayId: String(props.supportCase.displayId) },
}));
</script>

<template>
  <article
    class="flex flex-col gap-2 p-3 transition-colors border rounded-lg cursor-pointer bg-n-solid-2 border-n-weak hover:border-n-strong focus-within:border-n-strong"
    :data-testid="`case-board-card-${supportCase.displayId}`"
    @click="emit('select', supportCase)"
  >
    <router-link
      :to="linkTo"
      class="min-w-0 text-sm font-medium text-n-slate-12 line-clamp-2 hover:underline"
    >
      {{ supportCase.title }}
    </router-link>

    <div class="flex flex-wrap items-center gap-2">
      <span class="text-xs tabular-nums text-n-slate-10">
        {{ t('SUPPORT_CASES.LIST.DISPLAY_ID', { id: supportCase.displayId }) }}
      </span>
      <Label
        v-if="priority"
        compact
        :label="t(priority.labelKey)"
        :color="priority.color"
        data-testid="case-board-card-priority"
      >
        <template #icon>
          <Icon :icon="priority.icon" class="flex-shrink-0 size-3" />
        </template>
      </Label>
      <Label
        v-if="isOverdue"
        compact
        :label="t('SUPPORT_CASES.SLA.OVERDUE_IN_STAGE')"
        color="ruby"
        data-testid="case-board-card-overdue"
      >
        <template #icon>
          <Icon icon="i-lucide-clock-alert" class="flex-shrink-0 size-3" />
        </template>
      </Label>
      <span
        v-if="age"
        class="ltr:ml-auto rtl:mr-auto text-xs whitespace-nowrap text-n-slate-11"
        :aria-label="t('SUPPORT_CASES.BOARD.CARD_AGE', { age })"
      >
        {{ age }}
      </span>
    </div>

    <OwnerChip :owner="owner" />
  </article>
</template>
